import { useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  useCreate,
  useCustomMutation,
  useDelete,
  useList,
  useUpdate,
} from "@refinedev/core";
import { useTranslation } from "react-i18next";

import { PhoneInput } from "../../../components/PhoneInput";
import { usePageTitle } from "../../../hooks";
import { PageHeader } from "../../../components/common/PageHeader";
import { formatPhone } from "../../../utils/phone";

/**
 * Anchor a Select's dropdown to the trigger's parent so it renders inside
 * the surrounding Modal and isn't clipped behind it.
 */
const popupInsideModal = (node: HTMLElement) =>
  (node?.parentElement as HTMLElement) ?? document.body;

const AUDITOR_PHONE_COUNTRIES = [
  "ES", "IT", "PT", "ZA", "PE", "CL", "UY", "EG", "CY", "TR", "EC", "MX",
];

const { Title } = Typography;

interface AuditorAssignment {
  id: number;
  name: string;
  code: string;
  email: string;
  phone_number: string;
  user_type: 4 | 5;
  user_type_display: string;
  portfolio: number;
  audit_company: number | null;
  audit_company_name: string | null;
  allowed_portfolios: number[];
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

interface AuditCompanyOption {
  id: number;
  name: string;
}

interface FormValues {
  // Personal — `name` is composed server-side from first + last,
  // and `code` is auto-generated server-side from first + last as well.
  first_name: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  // Assignment
  user_type: 4 | 5;
  portfolio: number;
  audit_company: number | null;
  allowed_portfolios?: number[];
  is_active: boolean;
}

// Same labels as backend `apps/master_data/helpers.py:COUNTRIES`.
const PORTFOLIO_LABELS: Record<number, string> = {
  1: "España",
  2: "Perú",
  3: "Chile",
  4: "Sudáfrica",
  5: "Turquía",
  6: "Egipto",
  7: "Uruguay",
  8: "Portugal",
  9: "Italia",
};

const ALL_PORTFOLIOS = Object.entries(PORTFOLIO_LABELS).map(([k, label]) => ({
  value: Number(k),
  label,
}));

export const AuditorAssignmentAdminList = () => {
  const { t } = useTranslation();
  usePageTitle(t("admin_auditor_assignments.title"));

  const { modal, message } = AntdApp.useApp();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AuditorAssignment | null>(null);
  const [form] = Form.useForm<FormValues>();

  const assignmentsQuery = useList<AuditorAssignment>({
    resource: "v1/admin/auditor-assignments",
    pagination: { pageSize: 200 },
    sorters: [{ field: "name", order: "asc" }],
    filters: search
      ? [{ field: "search", operator: "contains" as any, value: search }]
      : [],
  });

  const companiesQuery = useList<AuditCompanyOption>({
    resource: "v1/admin/audit-companies",
    pagination: { pageSize: 500 },
    sorters: [{ field: "name", order: "asc" }],
  });

  const companyOptions = useMemo(
    () => [
      { value: null as number | null, label: t("admin_auditor_assignments.no_company") },
      ...((companiesQuery.query.data?.data ?? []).map((c: AuditCompanyOption) => ({
        value: c.id as number | null,
        label: c.name,
      })) as Array<{ value: number | null; label: string }>),
    ],
    [companiesQuery.query.data?.data, t],
  );

  const rows: AuditorAssignment[] = assignmentsQuery.query.data?.data ?? [];
  const isLoading = assignmentsQuery.query.isLoading;
  const refetch = () => assignmentsQuery.query.refetch();

  const { mutateAsync: createMutate } = useCreate();
  const { mutateAsync: updateMutate } = useUpdate();
  const { mutateAsync: deleteMutate } = useDelete();
  const { mutateAsync: customMutate } = useCustomMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const removeAuditor = async (record: AuditorAssignment) => {
    setDeletingId(record.id);
    try {
      await deleteMutate({
        resource: "v1/admin/auditor-assignments",
        id: record.id,
        successNotification: false,
        errorNotification: false,
      });
      message.success(
        t("admin_auditor_assignments.deleted", "Auditor eliminado"),
      );
      refetch();
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.originalError?.response?.data?.detail ??
        t(
          "admin_auditor_assignments.delete_error",
          "No se pudo eliminar el auditor.",
        );
      message.error(detail);
    } finally {
      setDeletingId(null);
    }
  };
  const [sendingId, setSendingId] = useState<number | null>(null);

  const userTypeWatch = Form.useWatch("user_type", form);
  const isManagerForm = userTypeWatch === 4;

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      user_type: 5,
      portfolio: 1,
      audit_company: null,
      allowed_portfolios: [],
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: AuditorAssignment) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      first_name: record.first_name || "",
      last_name: record.last_name || "",
      email: record.email,
      phone_number: record.phone_number || "",
      user_type: record.user_type,
      portfolio: record.portfolio,
      audit_company: record.audit_company,
      allowed_portfolios: record.allowed_portfolios ?? [],
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // form validation errors
    }

    // Normalise: type 5 has no allowed_portfolios.
    const payload: Record<string, unknown> = { ...values };
    if (values.user_type === 5) payload.allowed_portfolios = [];

    try {
      if (editing) {
        await updateMutate({
          resource: "v1/admin/auditor-assignments",
          id: editing.id,
          values: payload,
        });
        message.success(t("admin_auditor_assignments.save_success"));
      } else {
        await createMutate({
          resource: "v1/admin/auditor-assignments",
          values: payload,
        });
        message.success(t("admin_auditor_assignments.create_success"));
      }
      setModalOpen(false);
      refetch();
    } catch (e: any) {
      const data = e?.response?.data ?? e?.originalError?.response?.data;
      const msg = typeof data === "string"
        ? data
        : data
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join(" — ")
          : editing
            ? t("admin_auditor_assignments.save_error")
            : t("admin_auditor_assignments.create_error");
      message.error(msg);
    }
  };

  const updateField = async (
    record: AuditorAssignment,
    patch: Partial<
      Pick<
        AuditorAssignment,
        "audit_company" | "allowed_portfolios" | "is_active"
      >
    >,
  ) => {
    try {
      await updateMutate({
        resource: "v1/admin/auditor-assignments",
        id: record.id,
        values: patch,
      });
      message.success(t("admin_auditor_assignments.save_success"));
      refetch();
    } catch {
      message.error(t("admin_auditor_assignments.save_error"));
    }
  };

  const doSendCredentials = async (record: AuditorAssignment) => {
    setSendingId(record.id);
    try {
      const resp: any = await customMutate({
        url: `v1/admin/auditor-assignments/${record.id}/send-credentials/`,
        method: "post",
        values: {},
      });
      const sentTo: string =
        resp?.data?.sent_to ?? record.email ?? "";
      message.success(
        t("admin_auditor_assignments.send_credentials_success", { email: sentTo }),
      );
    } catch (e: any) {
      const data = e?.response?.data ?? e?.originalError?.response?.data;
      const detail =
        (typeof data === "object" && data?.detail) ||
        t("admin_auditor_assignments.send_credentials_error");
      message.error(String(detail));
    } finally {
      setSendingId(null);
    }
  };

  const confirmSendCredentials = (record: AuditorAssignment) => {
    modal.confirm({
      title: t("admin_auditor_assignments.send_credentials_modal_title"),
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>
            {t("admin_auditor_assignments.send_credentials_confirm", {
              email: record.email,
            })}
          </p>
          <p style={{ marginBottom: 0, fontSize: 13, color: "#8c8c8c" }}>
            {t("admin_auditor_assignments.send_credentials_recipient", {
              name: record.name,
              email: record.email,
            })}
          </p>
        </div>
      ),
      okText: t("admin_auditor_assignments.send_credentials_ok"),
      okButtonProps: { icon: <MailOutlined /> },
      cancelText: t("common.cancel"),
      centered: true,
      onOk: () => doSendCredentials(record),
    });
  };

  const columns = [
    {
      title: t("admin_auditor_assignments.auditor"),
      dataIndex: "name",
      key: "name",
      render: (_: unknown, record: AuditorAssignment) => (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
          <span style={{ fontWeight: 500 }}>{record.name}</span>
          {record.audit_company_name ? (
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {record.audit_company_name}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: "#bfbfbf" }}>
              {t("admin_auditor_assignments.no_company")}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t("admin_auditor_assignments.code"),
      dataIndex: "code",
      key: "code",
      width: 110,
    },
    {
      title: t("admin_auditor_assignments.username_col"),
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (v: string) => v || "—",
    },
    {
      title: t("admin_auditor_assignments.user_type"),
      dataIndex: "user_type_display",
      key: "user_type_display",
      width: 160,
      render: (label: string, row: AuditorAssignment) => (
        <Tag color={row.user_type === 4 ? "geekblue" : "blue"}>{label}</Tag>
      ),
    },
    {
      title: t("admin_auditor_assignments.email"),
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      render: (v: string) =>
        v ? v : <span style={{ color: "#bfbfbf", fontSize: 12 }}>—</span>,
    },
    {
      title: t("admin_auditor_assignments.phone"),
      dataIndex: "phone_number",
      key: "phone_number",
      width: 160,
      render: (v: string) =>
        v ? formatPhone(v) : <span style={{ color: "#bfbfbf", fontSize: 12 }}>—</span>,
    },
    {
      title: t("admin_auditor_assignments.portfolios"),
      key: "portfolios",
      render: (_: unknown, record: AuditorAssignment) => {
        // Manager (type 4): show allowed_portfolios when set, else fall back
        // to their main portfolio. Auditor (type 5): always main portfolio.
        const codes =
          record.user_type === 4 && record.allowed_portfolios?.length
            ? record.allowed_portfolios
            : [record.portfolio];
        if (codes.length === 1) {
          return PORTFOLIO_LABELS[codes[0]] ?? codes[0];
        }
        return (
          <Space size={[4, 4]} wrap>
            {codes.map((code) => (
              <Tag key={code} style={{ margin: 0 }}>
                {PORTFOLIO_LABELS[code] ?? code}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t("admin_auditor_assignments.is_active"),
      key: "is_active",
      width: 130,
      align: "center" as const,
      render: (_: unknown, record: AuditorAssignment) => {
        const active = record.is_active;
        return (
          <Tooltip
            title={
              active
                ? t("admin_auditor_assignments.click_to_deactivate")
                : t("admin_auditor_assignments.click_to_activate")
            }
          >
            <Tag
              icon={
                active ? (
                  <CheckCircleFilled style={{ color: "#52c41a" }} />
                ) : (
                  <CloseCircleFilled style={{ color: "#bfbfbf" }} />
                )
              }
              onClick={() => updateField(record, { is_active: !active })}
              style={{
                cursor: "pointer",
                margin: 0,
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 500,
                userSelect: "none",
                background: active ? "#f6ffed" : "#fafafa",
                borderColor: active ? "#b7eb8f" : "#d9d9d9",
                color: active ? "#389e0d" : "#8c8c8c",
              }}
            >
              {active
                ? t("admin_auditor_assignments.is_active_active")
                : t("admin_auditor_assignments.is_active_inactive")}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t("admin_auditor_assignments.actions"),
      key: "actions",
      width: 170,
      render: (_: unknown, record: AuditorAssignment) => {
        const hasEmail = !!record.email;
        return (
          <Space size={4}>
            <Tooltip title={t("common.edit")}>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => openEdit(record)}
              />
            </Tooltip>
            <Tooltip
              title={
                hasEmail
                  ? t("admin_auditor_assignments.send_credentials")
                  : t("admin_auditor_assignments.send_credentials_no_email")
              }
            >
              <Button
                icon={<MailOutlined />}
                size="small"
                disabled={!hasEmail}
                loading={sendingId === record.id}
                onClick={() => confirmSendCredentials(record)}
              />
            </Tooltip>
            <Popconfirm
              title={t(
                "admin_auditor_assignments.delete_confirm_title",
                "Eliminar auditor",
              )}
              description={
                <div style={{ maxWidth: 320 }}>
                  {t(
                    "admin_auditor_assignments.delete_confirm_body",
                    "Se eliminará {{name}} y su cuenta de login asociada. Los registros históricos (auditorías, tickets…) mantienen sus datos pero pierden el enlace al autor. No se puede deshacer.",
                    { name: record.name || record.username },
                  )}
                </div>
              }
              okText={t("common.yes", "Sí, eliminar")}
              cancelText={t("common.cancel", "Cancelar")}
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id,
              }}
              onConfirm={() => removeAuditor(record)}
            >
              <Tooltip title={t("common.delete", "Eliminar")}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<TeamOutlined />}
        accent="#1677FF"
        title={t("admin_auditor_assignments.title")}
        subtitle={t("admin_auditor_assignments.subtitle")}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("admin_auditor_assignments.create")}
          </Button>
        }
      />
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("common.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Space>

      <Modal
        title={
          editing
            ? t("admin_auditor_assignments.edit_title")
            : t("admin_auditor_assignments.create_title")
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Title level={5} style={{ marginTop: 0 }}>
            {t("admin_auditor_assignments.section_personal")}
          </Title>
          {!editing && (
            <p style={{ color: "#8c8c8c", marginTop: -4, marginBottom: 16, fontSize: 12 }}>
              {t("admin_auditor_assignments.account_autogen_hint")}
            </p>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.first_name")}
                name="first_name"
                rules={[{ required: true }]}
              >
                <Input placeholder={t("admin_auditor_assignments.ph_first_name")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.last_name")}
                name="last_name"
              >
                <Input placeholder={t("admin_auditor_assignments.ph_last_name")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.email")}
                name="email"
                rules={[{ required: true }, { type: "email" }]}
              >
                <Input placeholder={t("admin_auditor_assignments.ph_email")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.phone")}
                name="phone_number"
              >
                <PhoneInput
                  placeholder={t("admin_auditor_assignments.ph_phone")}
                  countryFilter={AUDITOR_PHONE_COUNTRIES}
                />
              </Form.Item>
            </Col>
          </Row>
          <Title level={5} style={{ marginTop: 8 }}>
            {t("admin_auditor_assignments.section_assignment")}
          </Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.user_type")}
                name="user_type"
                rules={[{ required: true }]}
              >
                <Select
                  getPopupContainer={popupInsideModal}
                  placeholder={t("admin_auditor_assignments.ph_user_type")}
                  options={[
                    {
                      value: 4,
                      label: t("admin_auditor_assignments.user_type_4"),
                    },
                    {
                      value: 5,
                      label: t("admin_auditor_assignments.user_type_5"),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.portfolio")}
                name="portfolio"
                rules={[{ required: true }]}
              >
                <Select
                  getPopupContainer={popupInsideModal}
                  placeholder={t("admin_auditor_assignments.ph_portfolio")}
                  options={ALL_PORTFOLIOS}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.company")}
                name="audit_company"
              >
                <Select
                  getPopupContainer={popupInsideModal}
                  options={companyOptions}
                  placeholder={t("admin_auditor_assignments.ph_company")}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.allowed_portfolios")}
                name="allowed_portfolios"
                extra={
                  isManagerForm
                    ? t("admin_auditor_assignments.allowed_portfolios_help")
                    : t("admin_auditor_assignments.manager_only")
                }
              >
                <Select
                  getPopupContainer={popupInsideModal}
                  mode="multiple"
                  options={ALL_PORTFOLIOS}
                  disabled={!isManagerForm}
                  maxTagCount="responsive"
                  placeholder={t("admin_auditor_assignments.ph_allowed_portfolios")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("admin_auditor_assignments.is_active")}
                name="is_active"
                valuePropName="checked"
                extra={t("admin_auditor_assignments.is_active_help")}
              >
                <Switch
                  checkedChildren={t("common.yes")}
                  unCheckedChildren={t("common.no")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
};

export default AuditorAssignmentAdminList;
